import { useCallback, useEffect, useRef, useState } from "react";
import { useJobChanges } from "../context/JobChanges";
import { jobKey, type JobReference, type JobReadResult } from "../Services/JobRefreshCoordinator";

/** Register only while the job view is mounted. All instances share one read. */
export function useObservedJob<T>(
  job: JobReference | undefined,
  read: (signal: AbortSignal) => Promise<JobReadResult & { value?: T }>,
  requester: boolean,
) {
  const coordinator = useJobChanges();
  const reader = useRef(read);
  reader.current = read;
  const key = job ? jobKey(job) : "";
  const [state, setState] = useState<{ key: string; owner: typeof coordinator; data?: T; error?: unknown }>({ key, owner: coordinator });
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (!job || !coordinator) return;
    return coordinator.observe(job, {
      requester,
      read: signal => reader.current(signal),
      delayed: setDelayed,
      received: result => setState({
        key, owner: coordinator,
        data: result.accessDenied ? undefined : result.value as T | undefined,
        error: result.accessDenied ? { status: 403, message: "You no longer have access to this job." } : undefined,
      }),
      failed: error => setState(previous => ({
        ...(previous.owner === coordinator && previous.key === key ? previous : {}),
        key, owner: coordinator, error,
      })),
    });
  }, [coordinator, key, requester]);
  const refetch = useCallback(() => {
    if (job) coordinator?.reconcile(job);
  }, [coordinator, key]);
  return {
    currentData: state.key === key && state.owner === coordinator ? state.data : undefined,
    error: state.key === key && state.owner === coordinator ? state.error : undefined,
    delayed,
    refetch,
  };
}
